import { action, observable } from 'mobx';
import { Container } from 'typescript-ioc';
import { ManagePageVM } from './_sidebar-content/_manage-page/manage-page.viewmodel';
import { EditHubVM } from './_sidebar-content/_edit-hub/edit-hub.viewmodel';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import { EventHubsDAO } from '../../../../api/implementations/event-hubs.dao';
import { IReactIntlProvider } from '../../../../infrastructure/react-intl-provider';
import { HubPreviewRefreshCallback } from '../_content/_hub-embed/hub-preview-sync.types';

export enum WebsiteBuilderSidebarTab {
    ManagePage = 'ManagePage',
    EditHub = 'EditHub',
}

export class WebsiteBuilderSidebarVM {
    @observable
    private minimized = false;
    @observable
    private activeTab: WebsiteBuilderSidebarTab = WebsiteBuilderSidebarTab.ManagePage;

    private managePageVM: ManagePageVM = null;
    private editHubVM: EditHubVM = null;

    constructor(
        private readonly versionId: number,
        private readonly accountId: number,
        private readonly eventHubId: number,
        private readonly onHubPreviewRefresh?: HubPreviewRefreshCallback,
    ) {}

    public dispose(): void {
        this.managePageVM?.dispose();
    }

    public getManagePageVM(): ManagePageVM {
        if (!this.managePageVM) {
            this.managePageVM = new ManagePageVM(this.versionId);
        }

        return this.managePageVM;
    }

    public getEditHubVM(): EditHubVM {
        if (!this.editHubVM) {
            this.editHubVM = new EditHubVM(
                this.accountId,
                this.eventHubId,
                Container.get(EventHubsDAO),
                Container.get(IReactIntlProvider),
                this.onHubPreviewRefresh,
                this.versionId,
            );
        }

        return this.editHubVM;
    }

    public canManageHubBranding(): boolean {
        return hasPermission(Permission.MANAGE_HUB_BRANDING);
    }

    public canConfigureRegistrationAndSignIn(): boolean {
        return hasPermission(Permission.CONFIGURE_EVENT_HUB_REGISTRATION_AND_SIGN_IN);
    }

    public canManagePagesAndModules(): boolean {
        return hasPermission(Permission.HUB_MANAGE_PAGES_AND_MODULES);
    }

    public canAccessEditHubTab(): boolean {
        return this.canManageHubBranding()
            || this.canConfigureRegistrationAndSignIn()
            || this.canManagePagesAndModules();
    }

    public isMinimized(): boolean {
        return this.minimized;
    }

    public getActiveTab(): WebsiteBuilderSidebarTab {
        return this.activeTab;
    }

    @action
    public toggleMinimize = (): void => {
        this.minimized = !this.minimized;
    };

    @action
    public setActiveTab = (tab: WebsiteBuilderSidebarTab): void => {
        if (tab === WebsiteBuilderSidebarTab.EditHub && !this.canAccessEditHubTab()) {
            this.activeTab = WebsiteBuilderSidebarTab.ManagePage;
            return;
        }
        this.activeTab = tab;
    };
}
